// This is a service that uses the Angular Hono client to make requests to the Dealcore API.
// And this is the only exception for eslint-disable-next-line @nx/enforce-module-boundaries!!!

// ********************************************************************************************
// ********************************************************************************************
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken, type Provider } from '@angular/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { AutocoreType } from '@hyundai/autocore-lib/router';
import { isHCM } from '../providers/platform.token';
import { injectAngularHonoClient } from '../utilities/ng-hono-client';
import { AddSearchAbo, FilterObject } from './api-types';
export interface AutocoreApiConfig {
    baseUrl: string;
}

export const AUTOCORE_API_CONFIG = new InjectionToken<AutocoreApiConfig>('APICORE_API_CONFIG');

export function provideAutocoreApiConfig(config: AutocoreApiConfig): Provider {
    return { provide: AUTOCORE_API_CONFIG, useValue: config };
}

@Injectable({ providedIn: 'root' })
export class VehicleApiService {
    http = inject(HttpClient);
    apiConfig = inject(AUTOCORE_API_CONFIG);

    api = injectAngularHonoClient<AutocoreType>({
        url: this.apiConfig.baseUrl,
        credentials: 'include',
        withCredentials: true,
    });

    initOptions = {
        init: { headers: { 'x-hcm': isHCM() ? '1' : '0' } },
    };

    private readonly hcmRequestArgs = {
        header: { 'x-hcm': isHCM() ? '1' : '0' },
    } as const;

    async getVehicleList(query?: { page?: number; limit?: number } & FilterObject, similarVehicleId?: number) {
        const request = await this.api.list.$get(
            {
                query: { data: JSON.stringify(query), similarVehicleId: similarVehicleId?.toString() },
            },
            this.initOptions,
        );

        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }

        return request.json();
    }
    async getFavorites(adIds: string[], dealerId?: string) {
        const request = await this.api.favorites.$get(
            {
                query: { adIds, ...(dealerId ? { dealerId } : {}) },
            },
            this.initOptions,
        );

        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }

        return request.json();
    }

    async getVehicleDetail(slug: string) {
        const request = await this.api.detail[':slug'].$get({ param: { slug } });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async addVehicleView(slug: string) {
        const request = await this.api['social-proof']['add-view'][':slug'].$post(
            { param: { slug } },
            this.initOptions,
        );
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async addSearchAbo(payload: AddSearchAbo) {
        const request = await this.api['search-abo']['add'].$post({
            ...this.hcmRequestArgs,
            json: payload,
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async doiSearchAbo(guid: string) {
        const request = await this.api['search-abo']['doi'][':guid'].$put(
            {
                param: { guid },
            },
            this.initOptions,
        );
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async useSearchAbo(guid: string) {
        const request = await this.api['search-abo']['use'][':guid'].$put(
            {
                param: { guid },
            },
            this.initOptions,
        );
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async cancelSearchAbo(guid: string) {
        const request = await this.api['search-abo']['cancel'][':guid'].$put(
            {
                param: { guid },
            },
            this.initOptions,
        );
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getNumberOfVehiclesViews(slug: string) {
        const request = await this.api['social-proof']['vehicle-views'][':slug'].$get(
            { param: { slug } },
            this.initOptions,
        );
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getNumberOfVehicles(filter: FilterObject = {}) {
        const request = await this.api.list.count.$get(
            {
                query: Object.keys(filter).length > 0 ? { data: JSON.stringify(filter) } : {},
            },
            this.initOptions,
        );

        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }

        return request.json();
    }

    async getPagePromise() {
        const request = await this.api.pages['hyundai-promise'].$get();
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getOverlayBatteryCertificate() {
        const request = await this.api.pages['overlay-battery-certificate'].$get();
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getModelList() {
        const request = await this.api.pages['models'].$get();
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    // Async getDebugModels() {
    //     Const request = await this.api.debug.$post({
    //         Json: {
    //             Price: { min: 1000, max: 100_000 },
    //             Page: 1,
    //             Limit: 9,
    //         },
    //     });
    //     Const d = request.json();
    //     Return d;
    // }

    async getFilter(dealerId?: string) {
        const request = await this.api.filters.$get({ query: { dealerId } });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }
}
